import { api } from '../api.js';
import { openModal, closeModal, showToast, esc } from '../ui.js';
import { debounce } from '../utils.js';
import { renderTrelloScopePickers, renderTrelloScopeBar, resolveEffectiveScope, matchesCardQuery } from '../lib/trello-scope-picker.js';
import { notConfiguredFrom, setupNoticeHtml } from '../lib/import-picker-shell.js';

// C1551 — this modal predates import-picker-shell.js and uses ui.js's openModal()/closeModal()
// instead (multi-step wizard, not the single-item picker chrome) — but notConfiguredFrom() and
// setupNoticeHtml() are pure functions with no dependency on that shell's overlay instance, so
// they're reused here directly. Same detection, same notice markup as the three import pickers.
function notConfiguredModal(err, title) {
  const notConfigured = notConfiguredFrom(err);
  if (!notConfigured) return false;
  openModal(title, setupNoticeHtml(notConfigured) + `
    <div class="modal-footer">
      <button class="btn btn-ghost" id="cancel-btn">Close</button>
    </div>`, { onMount(overlay) { overlay.querySelector('#cancel-btn').addEventListener('click', closeModal); } });
  return true;
}

// (C1552) association is the project's saved default (project_trello_boards); override is
// this modal session's own choice, if any (always wins — see resolveEffectiveScope in
// lib/trello-scope-picker.js, shared with trello-card-picker.js). The board is no longer
// permanent: renderBoardStep() is re-enterable from the card list via the scope bar's
// Change button, and a viewer whose association POST 403s gets a session-only override
// instead of the old dead end.
export function openTrelloImportModal(projectId, onImport) {
  const state = { projectId, onImport, association: null, override: null, notice: null, cards: [], selectedCards: [] };
  renderConnectStep(state);
}

function currentScope(state) {
  return resolveEffectiveScope(state.association, state.override);
}

async function renderConnectStep(state) {
  openModal('Import from Trello', '<p style="text-align:center;color:var(--c-overlay1)">Checking connection…</p>');

  let status, assocData;
  try {
    [status, assocData] = await Promise.all([
      api.get('/trello/status'),
      api.get('/projects/' + state.projectId + '/trello/board-association').catch(() => ({ association: null })),
    ]);
  } catch (err) {
    if (notConfiguredModal(err, 'Import from Trello')) return;
    closeModal();
    showToast(err.message || 'Failed to load Trello status', 'error');
    return;
  }

  if (!status.connected) {
    openModal('Import from Trello', `
      <div class="form-group">
        <p style="margin:0 0 12px">Connect your Trello account to import cards.</p>
        <p style="margin:0;font-size:.85rem;color:var(--c-overlay1)">You will be redirected to Trello to authorize access.</p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="cancel-btn">Cancel</button>
        <button class="btn btn-primary" id="connect-btn">Connect Trello</button>
      </div>`, {
      onMount(overlay) {
        overlay.querySelector('#cancel-btn').addEventListener('click', closeModal);
        overlay.querySelector('#connect-btn').addEventListener('click', async () => {
          const btn = overlay.querySelector('#connect-btn');
          btn.disabled = true; btn.textContent = 'Redirecting…';
          try {
            const { url } = await api.get('/trello/auth/initiate');
            window.location.href = url;
          } catch (err) {
            if (notConfiguredModal(err, 'Import from Trello')) return;
            showToast(err.message || 'Failed to initiate auth', 'error');
            btn.disabled = false; btn.textContent = 'Connect Trello';
          }
        });
      },
    });
    return;
  }

  state.association = assocData.association || null;
  if (currentScope(state)) {
    renderCardsStep(state);
  } else {
    renderBoardStep(state, { canGoBack: false });
  }
}

// (C1552) Re-enterable via the card list's scope bar Change button (canGoBack: true), not
// just on first entry. Chained dropdowns delegate to lib/trello-scope-picker.js, shared
// with trello-card-picker.js.
function renderBoardStep(state, { canGoBack = false } = {}) {
  openModal('Select Trello Board', '<div id="trello-import-scope-root"></div>', {
    onMount(overlay) {
      const root = overlay.querySelector('#trello-import-scope-root');
      const scope = currentScope(state);

      renderTrelloScopePickers(root, {
        idPrefix: 'trello-import',
        initial: scope ? { workspaceId: scope.workspaceId, boardId: scope.boardId } : {},
        confirmLabel: 'Continue',
        cancelLabel: canGoBack ? 'Back' : 'Cancel',
        onConfirm: (selection) => commitScope(state, selection),
        onCancel: () => { if (canGoBack) renderCardsStep(state); else closeModal(); },
        onError: (msg) => showToast(msg, 'error'),
      });
    },
  });
}

// Always attempts to persist the project default; a viewer's 403 (or any other save
// failure) degrades to a session-only override rather than blocking the import — same rule
// as trello-card-picker.js's commitScope(), see ai/architecture/tt-trello-integration.md's
// C1552 section. Never throws.
async function commitScope(state, { workspaceId, boardId, boardName }) {
  try {
    await api.post('/projects/' + state.projectId + '/trello/board-association', {
      trello_workspace_id: workspaceId,
      trello_board_id: boardId,
      trello_board_name: boardName,
    });
    state.association = { trello_workspace_id: workspaceId, trello_board_id: boardId, trello_board_name: boardName };
    state.override = { workspaceId, boardId, boardName, persisted: true };
  } catch (err) {
    state.override = { workspaceId, boardId, boardName, persisted: false };
    state.notice = err.status === 403
      ? 'Only project editors can set the project default board — using this board for this session only.'
      : (err.message ? `Could not save this board as the project default (${err.message}) — using it for this session only.` : 'Could not save this board as the project default — using it for this session only.');
  }
  renderCardsStep(state);
}

async function renderCardsStep(state) {
  openModal('Select Cards', '<p style="text-align:center;color:var(--c-overlay1)">Loading cards…</p>');

  const scope = currentScope(state);
  let cards;
  try {
    cards = await api.get('/trello/cards?boardId=' + encodeURIComponent(scope.boardId));
  } catch (err) {
    if (notConfiguredModal(err, 'Select Cards')) return;
    openModal('Select Cards', `
      <p style="text-align:center;color:var(--c-overlay1)">${esc(err.message || 'Failed to load cards.')}</p>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="cancel-btn">Cancel</button>
        <button class="btn btn-primary" id="change-board-btn">Change board</button>
      </div>`, {
      onMount(overlay) {
        overlay.querySelector('#cancel-btn').addEventListener('click', closeModal);
        overlay.querySelector('#change-board-btn').addEventListener('click', () => renderBoardStep(state, { canGoBack: true }));
      },
    });
    return;
  }

  state.cards = cards;
  // One-shot notice from a just-completed board change (e.g. the 403 session-only
  // fallback) — surfaced as a toast right before the cards modal opens below.
  if (state.notice) {
    showToast(state.notice, 'error');
    state.notice = null;
  }

  function labelChip(l) {
    const bg = l.color ? `var(--trello-${esc(l.color)}, var(--c-overlay1))` : 'var(--c-overlay1)';
    return `<span style="background:${bg};color:#fff;font-size:.7rem;padding:1px 7px;border-radius:9px;white-space:nowrap">${esc(l.name || l.color || '')}</span>`;
  }

  function cardListHTML(list) {
    if (!list.length) return '<p style="text-align:center;color:var(--c-overlay1);padding:12px 0">No cards found</p>';
    return list.map(c => {
      const chips = (c.labels || []).filter(l => l.name || l.color).map(labelChip).join('');
      return `<label class="trello-card-row">
        <input type="checkbox" class="trello-card-cb" value="${esc(c.id)}">
        <span style="flex:1;font-size:.9rem">${esc(c.name)}</span>
        ${c.listName ? `<span class="trello-card-list-badge">${esc(c.listName)}</span>` : ''}
        <span class="trello-card-status-badge ${c.dueComplete ? 'done' : 'open'}">${c.dueComplete ? 'Done' : 'Open'}</span>
        ${chips ? `<span style="display:flex;gap:4px;flex-wrap:wrap">${chips}</span>` : ''}
      </label>`;
    }).join('');
  }

  openModal('Select Cards', `
    ${renderTrelloScopeBar({ boardName: scope.boardName, boardId: scope.boardId, persisted: scope.persisted, changeId: 'change-board-btn' })}
    <div class="form-group">
      <input class="form-input" id="card-search" type="search" placeholder="Search cards by name or description…">
    </div>
    <div class="trello-card-list" id="card-list">${cardListHTML(cards)}</div>
    <div class="modal-footer">
      <button class="btn btn-ghost" id="cancel-btn">Cancel</button>
      <button class="btn btn-primary" id="next-btn" disabled>Next</button>
    </div>`, {
    onMount(overlay) {
      const list = overlay.querySelector('#card-list');
      const nextBtn = overlay.querySelector('#next-btn');
      const searchInput = overlay.querySelector('#card-search');

      overlay.querySelector('#cancel-btn').addEventListener('click', closeModal);
      overlay.querySelector('#change-board-btn').addEventListener('click', () => renderBoardStep(state, { canGoBack: true }));

      function updateNext() {
        nextBtn.disabled = list.querySelectorAll('.trello-card-cb:checked').length === 0;
      }

      list.addEventListener('change', updateNext);

      searchInput.addEventListener('input', () => {
        const q = searchInput.value.trim();
        const filtered = q ? cards.filter((c) => matchesCardQuery(c, q)) : cards;
        list.innerHTML = cardListHTML(filtered);
        updateNext();
      });

      nextBtn.addEventListener('click', () => {
        const checked = new Set([...list.querySelectorAll('.trello-card-cb:checked')].map(cb => cb.value));
        state.selectedCards = cards.filter(c => checked.has(c.id));
        renderTargetStep(state);
      });
    },
  });
}

function renderTargetStep(state) {
  openModal('Import Destination', `
    <style>
      .task-result-item:hover{background:var(--c-surface1)}
    </style>
    <div class="form-group">
      <p style="margin:0 0 10px"><strong>${state.selectedCards.length}</strong> card(s) selected</p>
      <div style="display:flex;flex-direction:column;gap:8px">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer">
          <input type="radio" name="target-mode" value="new" checked> Create new tasks
        </label>
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer">
          <input type="radio" name="target-mode" value="existing"> Add as subtasks of existing task
        </label>
      </div>
    </div>
    <div id="task-search-wrap" class="form-group" style="display:none">
      <input class="form-input" id="task-search" type="search" placeholder="Search tasks by title…">
      <ul id="task-results" style="list-style:none;padding:0;margin:4px 0;max-height:150px;overflow-y:auto;border:1px solid var(--c-border);border-radius:6px;display:none"></ul>
    </div>
    <div class="modal-footer">
      <button class="btn btn-ghost" id="back-btn">Back</button>
      <button class="btn btn-primary" id="import-btn">Import</button>
    </div>`, {
    onMount(overlay) {
      let selectedTargetTaskId = null;

      const searchWrap = overlay.querySelector('#task-search-wrap');
      const taskSearch = overlay.querySelector('#task-search');
      const taskResults = overlay.querySelector('#task-results');
      const importBtn = overlay.querySelector('#import-btn');

      overlay.querySelector('#back-btn').addEventListener('click', () => renderCardsStep(state));

      overlay.querySelectorAll('input[name="target-mode"]').forEach(r => {
        r.addEventListener('change', () => {
          const mode = overlay.querySelector('input[name="target-mode"]:checked').value;
          searchWrap.style.display = mode === 'existing' ? 'block' : 'none';
          if (mode === 'new') {
            selectedTargetTaskId = null;
            taskSearch.value = '';
            taskResults.style.display = 'none';
            taskResults.innerHTML = '';
          }
        });
      });

      const doSearch = debounce(async () => {
        const q = taskSearch.value.trim();
        if (!q) { taskResults.style.display = 'none'; taskResults.innerHTML = ''; return; }
        try {
          const data = await api.get(
            '/projects/' + state.projectId + '/tasks?q=' + encodeURIComponent(q) + '&fields=summary&limit=20'
          );
          const tasks = data.tasks || [];
          if (!tasks.length) {
            taskResults.innerHTML = '<li style="padding:6px 10px;color:var(--c-overlay1)">No tasks found</li>';
            taskResults.style.display = 'block';
            return;
          }
          taskResults.innerHTML = tasks.map(t =>
            `<li class="task-result-item" data-id="${esc(String(t.id))}"
              style="padding:6px 10px;cursor:pointer;border-radius:4px">${esc(t.title)}</li>`
          ).join('');
          taskResults.style.display = 'block';
          taskResults.querySelectorAll('.task-result-item[data-id]').forEach(li => {
            li.addEventListener('click', () => {
              selectedTargetTaskId = parseInt(li.dataset.id, 10);
              taskSearch.value = li.textContent.trim();
              taskResults.style.display = 'none';
            });
          });
        } catch {
          taskResults.innerHTML = '';
          taskResults.style.display = 'none';
        }
      }, 250);

      taskSearch.addEventListener('input', () => { selectedTargetTaskId = null; doSearch(); });

      importBtn.addEventListener('click', async () => {
        const mode = overlay.querySelector('input[name="target-mode"]:checked').value;
        if (mode === 'existing' && !selectedTargetTaskId) {
          showToast('Select a target task first', 'error');
          return;
        }
        importBtn.disabled = true; importBtn.textContent = 'Importing…';
        try {
          const result = await api.post('/projects/' + state.projectId + '/trello/import', {
            cards: state.selectedCards,
            targetTaskId: mode === 'existing' ? selectedTargetTaskId : null,
          });
          closeModal();
          showToast(`Imported ${result.imported} task(s)`, 'success');
          if (state.onImport) state.onImport(result);
        } catch (err) {
          importBtn.disabled = false; importBtn.textContent = 'Import';
          showToast(err.message || 'Import failed', 'error');
        }
      });
    },
  });
}
